import { customerAuthSchema, type CustomerAuth } from "./customer-auth";
import { z } from "zod";
import {
  settingsResponseSchema,
  supportRequestCapabilitiesSchema,
  proactiveRuleSchema,
  type ProactiveRule,
  type SettingsResponse,
} from "@go-novus/admin-api";

/**
 * 对应 `apps/shopify/contract/schemas/handshake-token-response.schema.json`。
 * 未提供 uid 时只读取外观；关闭时不签发身份令牌。
 */
const handshakeResponseSchema = z.object({
  settings: settingsResponseSchema.extend({
    // 商家写入仍用严格的共享契约；店面读取逐条隔离坏规则，不能连聊天一起关掉。
    proactiveRules: z.array(z.unknown()).transform((value): ProactiveRule[] => {
      const rules: ProactiveRule[] = [];
      for (const entry of value) {
        const parsed = proactiveRuleSchema.safeParse(entry);
        if (parsed.success && !rules.some((rule) => rule.id === parsed.data.id))
          rules.push(parsed.data);
      }
      return rules;
    }),
  }),
  disabled: z.boolean(),
  capabilities: supportRequestCapabilitiesSchema,
});

const handshakeTokenSchema = handshakeResponseSchema.extend({
  token: z.string().min(1),
  expires_in: z.number().int().positive(),
  socket_server_url: z.string().min(1),
  auth: customerAuthSchema,
});

/** 令牌里的声明；`tenant_id` 由本应用签发，可能是数字也可能是字符串。 */
const tokenPayloadSchema = z.object({
  tenant_id: z.union([z.number(), z.string()]),
  uid: z.string(),
  exp: z.number(),
});

export interface HandshakeOk {
  status: "ok";
  token: string;
  socketUrl: string;
  /** 发 `send-message` 帧必须带的店铺标识，只能从令牌里取。 */
  tenantId: number;
  disabled: boolean;
  settings: SettingsResponse;
  capabilities: z.infer<typeof supportRequestCapabilitiesSchema>;
  auth: CustomerAuth;
}

export interface HandshakeConfiguration {
  status: "configured" | "disabled";
  settings: SettingsResponse;
  capabilities: z.infer<typeof supportRequestCapabilitiesSchema>;
}

export interface HandshakeFailed {
  status: "failed";
  /** transport: 请求或解 JSON 失败；http: 非 2xx；schema: 响应不合契约；token: 令牌不可用。 */
  reason: "transport" | "http" | "schema" | "token";
  detail: string;
}

export type HandshakeResult =
  HandshakeOk | HandshakeFailed | HandshakeConfiguration;

/**
 * Liquid 渲染时拿到的自报信号，随握手入站只作诊断对照：
 * 不构成身份依据，不进令牌，服务端记录后即弃（GON-126）。
 */
export interface HandshakeObservation {
  /**
   * `customer.id`：页面带登录顾客时是数字串，确认未登录是空串；
   * 宿主没给（旧资源、非 Liquid 宿主）则缺省，服务端记 unreported。
   */
  liquidCustomerId?: string;
  /** `shop.customer_accounts_enabled`。 */
  customerAccountsEnabled?: boolean;
}

/** 从宿主配置里挑出自报信号字段；两处握手调用点共用同一份挑法。 */
export function toHandshakeObservation(config: {
  liquidCustomerId?: string;
  customerAccountsEnabled?: boolean;
}): HandshakeObservation {
  return {
    liquidCustomerId: config.liquidCustomerId,
    customerAccountsEnabled: config.customerAccountsEnabled,
  };
}

/**
 * 握手请求地址：uid 在前，Liquid 自报参数随后，顺序固定——
 * Liquid 预取和 storefront 入口靠同一份逻辑判断地址是否一致。
 */
export function handshakeRequestUrl(
  handshakeUrl: string,
  uid: string | undefined,
  observation?: HandshakeObservation,
): string {
  const parts: string[] = [];
  if (uid) parts.push(`uid=${encodeURIComponent(uid)}`);
  if (observation?.liquidCustomerId !== undefined)
    parts.push(
      `liquid_customer_id=${encodeURIComponent(observation.liquidCustomerId)}`,
    );
  if (observation?.customerAccountsEnabled !== undefined)
    parts.push(
      `customer_accounts_enabled=${String(observation.customerAccountsEnabled)}`,
    );
  if (parts.length === 0) return handshakeUrl;
  const separator = handshakeUrl.includes("?") ? "&" : "?";
  return `${handshakeUrl}${separator}${parts.join("&")}`;
}

export async function requestHandshake(
  handshakeUrl: string,
  uid: string | undefined,
  fetchImpl: typeof fetch = fetch,
  observation?: HandshakeObservation,
): Promise<HandshakeResult> {
  let body: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetchImpl(
        uid === undefined
          ? handshakeRequestUrl(handshakeUrl, undefined, observation)
          : handshakeUrl,
        uid === undefined
          ? {
              credentials: "same-origin",
              method: "GET",
              signal: AbortSignal.timeout(8_000),
            }
          : {
              credentials: "same-origin",
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({
                uid,
                liquid_customer_id: observation?.liquidCustomerId,
                customer_accounts_enabled: observation?.customerAccountsEnabled,
              }),
              signal: AbortSignal.timeout(8_000),
            },
      );
      if (!response.ok) {
        if (attempt === 0 && [502, 503, 504].includes(response.status)) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          continue;
        }
        return {
          status: "failed",
          reason: "http",
          detail: `HTTP ${String(response.status)}`,
        };
      }
      body = await response.json();
      break;
    } catch (error) {
      if (attempt === 0 && !(error instanceof SyntaxError)) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        continue;
      }
      return { status: "failed", reason: "transport", detail: describe(error) };
    }
  }

  const parsed = handshakeResponseSchema.safeParse(body);
  if (!parsed.success) {
    return {
      status: "failed",
      reason: "schema",
      detail: z.prettifyError(parsed.error),
    };
  }

  if (parsed.data.disabled)
    return {
      status: "disabled",
      settings: parsed.data.settings,
      capabilities: parsed.data.capabilities,
    };
  if (!uid)
    return {
      status: "configured",
      settings: parsed.data.settings,
      capabilities: parsed.data.capabilities,
    };
  const tokenResponse = handshakeTokenSchema.safeParse(body);
  if (!tokenResponse.success)
    return {
      status: "failed",
      reason: "schema",
      detail: z.prettifyError(tokenResponse.error),
    };

  const claims = readTokenClaims(tokenResponse.data.token, uid);
  if (claims === null) {
    return {
      status: "failed",
      reason: "token",
      detail: "令牌声明缺失、与请求不符或已过期",
    };
  }

  return {
    status: "ok",
    token: tokenResponse.data.token,
    socketUrl: tokenResponse.data.socket_server_url,
    tenantId: claims.tenantId,
    disabled: parsed.data.disabled,
    settings: parsed.data.settings,
    capabilities: parsed.data.capabilities,
    auth: tokenResponse.data.auth,
  };
}

interface TokenClaims {
  tenantId: number;
}

/** 只读声明,不验签;签名由 message-center 校验。返回 null 表示声明不可信。 */
export function readTokenClaims(
  token: string,
  uid: string,
): TokenClaims | null {
  const payload = tokenPayloadSchema.safeParse(decodeTokenPayload(token));
  if (!payload.success || payload.data.uid !== uid) return null;
  if (payload.data.exp * 1000 <= Date.now()) return null;
  const tenantId = Number(payload.data.tenant_id);
  if (!Number.isInteger(tenantId)) return null;
  return { tenantId };
}

/**
 * 令牌里的 tenant_id，发 `send-message` 帧必须带上。
 * uid 对不上或已过期的令牌连不上 Socket，这里就当读不到。
 * 后台 Playground 拿到的是同一个签发核心发的令牌，所以走的也是这一段。
 */
export function readTokenTenantId(token: string, uid: string): number | null {
  return readTokenClaims(token, uid)?.tenantId ?? null;
}

/** 只读声明，不验签：签名由 message-center 校验。 */
function decodeTokenPayload(token: string): unknown {
  const parts = token.split(".");
  const encoded = parts[1];
  if (parts.length !== 3 || encoded === undefined) return null;
  const base64 = encoded
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(encoded.length / 4) * 4, "=");
  try {
    return JSON.parse(atob(base64)) as unknown;
  } catch {
    return null;
  }
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
